import {
  Heading,
  Button,
  Box,
  Image,
  IconButton,
  List,
  ListItem,
  ListIcon,
  Text,
} from '@everlywell/ui-kit';
import {
  Circle,
  DeviceMobileSpeaker,
  Stethoscope,
  TestTube,
} from '@phosphor-icons/react';
import DynamicIcon from 'components/DynamicIcon';
import { usePrePurchaseQuizContext } from 'contexts/PrePurchaseQuiz/PrePurchaseQuizContext';
import { Link } from 'gatsby';
import React from 'react';

import mobileModalImage from '../../InitialQuizScreen/images/mobile-squares-bg-image.png';
import desktopModalImage from '../../InitialQuizScreen/images/squares-bg-image.png';

export type YouAreNotEligibleProps = {
  firstName: string;
};

const BACKGROUND_COLOR = 'sky.dark';

/**
 * This component adds the You Are Not Eligible screen the pre-purchase quiz modal.
 */

// TODO:
// - move content to Contentful and fetch it from there
// - Use components/BulletPointWithIcon/BulletPointWithIcon for icons with background
function YouAreNotEligible(props: YouAreNotEligibleProps): JSX.Element {
  const { firstName } = props;

  const {
    toggleModal,
    resetState,
    prevStep,
    setIsAnswerIneligible,
    currentStep,
    resetWeightData,
  } = usePrePurchaseQuizContext();

  const handleModalClose = () => {
    toggleModal(false);
    resetState();
  };

  const handlePrevStep = () => {
    // Reset weight data if the user is coming back from the weight question
    if (currentStep === 2) {
      resetWeightData();
    }

    setIsAnswerIneligible(false);
    prevStep();
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      backgroundColor={BACKGROUND_COLOR}
      justifyContent={['flex-end', null, 'center']}
      textAlign="center"
      height="100vh"
      position="relative"
      paddingTop={[5, null, 0]}
    >
      <IconButton
        colorScheme="whiteCS"
        size="sm"
        aria-label="previous-step"
        icon={<DynamicIcon icon="ArrowLeft" color="tints.uiGrey" />}
        onClick={handlePrevStep}
        width="40px"
        height="40px"
        position="absolute"
        top="20px"
        left={['24px', null, '70px']}
      />
      <IconButton
        colorScheme="whiteCS"
        size="sm"
        aria-label="close"
        icon={<DynamicIcon icon="X" color="tints.uiGrey" />}
        onClick={handleModalClose}
        width="40px"
        height="40px"
        position="absolute"
        top="20px"
        right={['24px', null, '70px']}
      />
      <Box paddingX="5">
        <Heading
          as="h1"
          size="xl"
          color="violet.wash"
          opacity="0.6"
          marginBottom="1"
        >
          We’re sorry {firstName},
        </Heading>
        <Heading
          as="h2"
          size="xl"
          color="tints.white"
          marginBottom={['8', '10']}
          maxWidth={['100%', '650px']}
        >
          you’re not eligible for this program. You might want to explore
          Everlywell+
        </Heading>
        <List color="tints.white" marginBottom={['8', '10']} textAlign="left">
          <ListItem
            marginBottom={['5', '6']}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap="8"
          >
            <ListIcon
              as={Circle}
              color="tints.white"
              weight="fill"
              width="40px"
              height="40px"
              position="absolute"
              zIndex="0"
            />
            <ListIcon
              as={TestTube}
              color="tints.black"
              position="relative"
              zIndex="1"
              width="20px"
              height="20px"
              right="-10px"
              top="0"
            />
            <Text>1 credit to redeem on Everlywell test kits per month</Text>
          </ListItem>
          <ListItem
            marginBottom={['5', '6']}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap="8"
          >
            <ListIcon
              as={Circle}
              color="tints.white"
              weight="fill"
              width="40px"
              height="40px"
              position="absolute"
              zIndex="0"
            />
            <ListIcon
              as={Stethoscope}
              color="tints.black"
              position="relative"
              zIndex="1"
              width="20px"
              height="20px"
              right="-10px"
              top="0"
            />
            <Text>One virtual consultation with a doctor each month</Text>
          </ListItem>
          <ListItem
            marginBottom={['5', '6']}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            gap="8"
          >
            <ListIcon
              as={Circle}
              color="tints.white"
              weight="fill"
              width="40px"
              height="40px"
              position="absolute"
              zIndex="0"
            />
            <ListIcon
              as={DeviceMobileSpeaker}
              color="tints.black"
              position="relative"
              zIndex="1"
              width="20px"
              height="20px"
              right="-10px"
              top="0"
            />
            <Text>
              Access to the Everlywell App to monitor your progress and manage
              your membership
            </Text>
          </ListItem>
        </List>
        <Button
          variant="primary"
          size={['wide', 'md']}
          colorScheme="whiteCS"
          as={Link}
          to="/monthly-at-home-lab-testing-membership/"
          // This is a fix to make the button clickable on desktop, even if the bottom image shifts and covers the button
          position="relative"
          zIndex="5"
        >
          Explore Everlywell+
        </Button>
      </Box>
      <Image
        src={mobileModalImage}
        alt="Image of a orange hexagon pattern" // Show this image on mobile. TODO: This is a temporary hack until we move the images to Contentful.
        display={['block', null, 'none']}
        maxWidth="100%"
      />
      <Image
        src={desktopModalImage}
        alt="Image of a orange hexagon pattern" // Show this image on desktop. TODO: This is a temporary hack until we move the images to Contentful.
        display={['none', null, 'block']}
        maxWidth="900px"
        position="absolute"
        bottom="0"
      />
    </Box>
  );
}

export default YouAreNotEligible;
